// src/pages/home/Home.jsx
import React, { useEffect, useState } from 'react';
import {
    Paper, Typography, Button, Chip, Stack, Card, CardContent, Divider,
    Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Alert,
    IconButton, Tooltip, Box,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link as RouterLink } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';

import s from './Home.module.scss';
import { listProducts, health as apiHealth /* , listShopProducts */ } from '../../api';
import { formatWeight } from '../products/utils/formatters';

export default function Home() {
    const [health, setHealth] = useState(null);
    const [loadingHealth, setLoadingHealth] = useState(true);

    const [latestSite, setLatestSite] = useState([]); // товари з site=company
    const [loadingSite, setLoadingSite] = useState(true);

    // const [latestShop, setLatestShop] = useState([]);
    // const [loadingShop, setLoadingShop] = useState(true);

    const [err, setErr] = useState('');

    const fetchHealth = async () => {
        try {
            setLoadingHealth(true);
            setErr('');
            const json = await apiHealth();
            setHealth(json);
        } catch {
            setHealth({ ok: false });
            setErr('Не вдалося отримати статус API');
        } finally {
            setLoadingHealth(false);
        }
    };

    const fetchLatestSite = async () => {
        try {
            setLoadingSite(true);
            setErr('');
            const res = await listProducts({
                site: 'company',            // ⬅️ ключова правка
                limit: 8,
                page: 1,
                sort: 'createdAt',
                dir: 'desc',
            });
            const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
            setLatestSite(items.slice(0, 8));
        } catch {
            setErr('Не вдалося завантажити останні товари сайту');
            setLatestSite([]);
        } finally {
            setLoadingSite(false);
        }
    };

    // const fetchLatestShop = async () => { ... };

    useEffect(() => {
        fetchHealth();
        fetchLatestSite();
        // fetchLatestShop();
    }, []);

    return (
        <div className={s.screen}>
            {/* ===== Header row ===== */}
            <Paper elevation={0} className={s.header}>
                <div className={s.headerLeft}>
                    <Typography variant="h5" className={s.title}>
                        Головна адмінки
                    </Typography>
                    <Typography variant="body2" className={s.subtitle}>
                        Короткий огляд стану системи та швидкі дії.
                    </Typography>
                </div>
                <div className={s.headerRight}>
                    <Tooltip title="Оновити дані">
                        <IconButton
                            onClick={() => {
                                fetchHealth();
                                fetchLatestSite();
                                // fetchLatestShop();
                            }}
                            className={s.refreshBtn}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </Paper>

            {err && (
                <Alert severity="warning" className={s.alert}>
                    {err}
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* ===== Quick Actions ===== */}
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card elevation={0} className={s.card}>
                        <CardContent className={s.cardContent}>
                            <div className={s.cardHead}>
                                <Typography variant="subtitle1" className={s.cardTitle}>
                                    Швидкі дії
                                </Typography>
                                <Chip size="small" label="Admin" className={s.badge} />
                            </div>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    component={RouterLink}
                                    to="/products"
                                    variant="contained"
                                    startIcon={<Inventory2Icon />}
                                    className={s.btnPrimary}
                                >
                                    Вся продукція
                                </Button>

                                <Button
                                    component={RouterLink}
                                    to="/products/new"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    className={s.btnLight}
                                >
                                    Додати продукцію
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ===== API Health ===== */}
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card elevation={0} className={s.card}>
                        <CardContent className={s.cardContent}>
                            <div className={s.cardHead}>
                                <Typography variant="subtitle1" className={s.cardTitle}>
                                    Статус API
                                </Typography>
                            </div>

                            <div className={s.kvGrid}>
                                <div className={s.kv}>
                                    <span>ok</span>
                                    <strong>
                                        {loadingHealth ? <Skeleton width={40} /> : String(!!health?.ok)}
                                    </strong>
                                </div>

                                <div className={s.kv}>
                                    <span>БД</span>
                                    <strong>
                                        {loadingHealth ? <Skeleton width={40} /> : String(health?.db)}
                                    </strong>
                                </div>

                                <div className={s.kv}>
                                    <span>mode</span>
                                    <strong>
                                        {loadingHealth ? (
                                            <Skeleton width={80} />
                                        ) : (
                                            health?.mode || (health?.db ? 'db' : 'unknown')
                                        )}
                                    </strong>
                                </div>
                            </div>

                            <Divider className={s.divider} />

                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={fetchHealth} startIcon={<RefreshIcon />}>
                                    Перевірити ще раз
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ===== Info / tips ===== */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Card elevation={0} className={s.cardAccent}>
                        <CardContent className={s.cardContent}>
                            <Typography variant="subtitle1" className={s.cardTitle}>
                                Корисна інформація
                            </Typography>
                            <Typography variant="body2" className={s.muted}>
                                Налаштування брендів, імпорт з CSV, розширені звіти — з’являться в наступних оновленнях.
                            </Typography>

                            <Box className={s.callout}>
                                Потрібно додати ще віджети чи швидкі лінки? Кажи — піджену під твій флоу.
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ===== Latest site products ===== */}
                <Grid size={{ xs: 12 }}>
                    <Card elevation={0} className={s.cardWide}>
                        <CardContent className={s.cardContent}>
                            <div className={s.cardHead}>
                                <Typography variant="subtitle1" className={s.cardTitle}>
                                    Останні товари (сайт)
                                </Typography>

                                <Button component={RouterLink} to="/products" className={s.linkBtn}>
                                    відкрити всі
                                </Button>
                            </div>

                            <Paper variant="outlined" className={s.tableWrap}>
                                <Table size="small" className={s.table}>
                                    <TableHead>
                                        <TableRow className={s.trHead}>
                                            <TableCell>Назва</TableCell>
                                            <TableCell>Бренд</TableCell>
                                            <TableCell align="right">Вага</TableCell>
                                            <TableCell align="right">Дія</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {loadingSite ? (
                                            Array.from({ length: 5 }).map((_, i) => (
                                                <TableRow key={i}>
                                                    <TableCell><Skeleton width="80%" /></TableCell>
                                                    <TableCell><Skeleton width="60%" /></TableCell>
                                                    <TableCell align="right"><Skeleton width={60} /></TableCell>
                                                    <TableCell align="right"><Skeleton width={100} /></TableCell>
                                                </TableRow>
                                            ))
                                        ) : latestSite.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography variant="body2" className={s.empty}>
                                                        Поки немає товарів.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            latestSite.map((p) => (
                                                <TableRow key={p.id} hover>
                                                    <TableCell className={s.nameCell}>
                                                        {p.name || p.title || 'Без назви'}
                                                    </TableCell>
                                                    <TableCell>{p.brand || '—'}</TableCell>
                                                    <TableCell align="right">{formatWeight(p)}</TableCell>
                                                    <TableCell align="right">
                                                        <Button size="small" component={RouterLink} to={`/products/${p.id}`}>
                                                            Редагувати
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ===== Latest shop products (опційно) ===== */}
                {/*
        <Grid size={{ xs: 12 }}>
          ...
        </Grid>
        */}
            </Grid>
        </div>
    );
}
